import { Store } from './state';
import type { GameState, HistoryEntry, PreparedImageRef, RuntimePiece, Theme } from './state';
import { computeLayout, scatterWithSeed, type Viewport } from './layout';
import { generatePuzzle } from './puzzleGenerator';
import { gridForDifficulty } from './grid';
import type { DifficultyLevel } from './grid';
import { cachePiece, type CachedPiece, type PieceSourceImage } from '../render/pieceCache';
import { Renderer } from '../render/Renderer';
import { HitTester } from '../render/hitTest';
import { InputController, type InputCallbacks } from '../interaction/InputController';
import { createOverlay, type Overlay } from '../ui/overlay';
import { createPreviewBanner, type PreviewBanner } from '../ui/preview';
import { createCanvas, get2D } from '../render/canvasUtil';
import { EAR_MAX, buildPiecePath2D } from '../render/piecePath';
import { generateWoodTexture, generateNoiseTexture } from '../render/textures';
import { pickHintTarget, computeUndoDelta } from './actions';
import { hashStringToSeed } from './rng';
import { bestRecordKey, loadBest, saveBest, isBetter } from './progress';
import { sfx } from '../audio/sfx';
import { showToast } from '../ui/toast';
import type { GameActions } from '../ui/toolbar';

export interface StartOptions {
  difficulty: DifficultyLevel;
  rotateMode: boolean;
  imageKey: string;
}

function initialIdleState(): GameState {
  return {
    status: 'idle',
    image: null,
    imageKey: '',
    config: { difficulty: 'medium', rotateMode: true, showReference: false, referenceOpacity: 0.35 },
    pieces: [],
    puzzleSeed: 0,
    pieceWidth: 0,
    pieceHeight: 0,
    boardX: 0,
    boardY: 0,
    boardW: 0,
    boardH: 0,
    trayX: 0,
    trayY: 0,
    trayW: 0,
    trayH: 0,
    startedAt: null,
    elapsedMs: 0,
    paused: false,
    moves: 0,
    placedCount: 0,
    draggingId: null,
    dragOffsetX: 0,
    dragOffsetY: 0,
    topZ: 0,
    hintsLeft: 3,
    hintTargetId: null,
    hintUntil: 0,
    history: [],
    muted: false,
    theme: 'cozy',
    zoom: 1,
    trayScroll: 0,
    maxTrayScroll: 0,
    trayScale: 1,
  };
}

/**
 * 游戏总控：装配状态 / 渲染 / 交互；驱动主循环、状态机与计时。
 *
 * 状态机：idle → ready →（首次拖拽）playing →（全部吸附）completed。
 * 计时靠主循环累加 elapsedMs（不依赖 startedAt 减法），暂停精确。
 */
export class Game implements GameActions {
  readonly store: Store;
  readonly overlayEl: HTMLElement;
  readonly previewEl: HTMLElement;

  private completeInfo: { timeMs: number; moves: number; best: boolean } | null = null;
  private readonly overlay: Overlay;
  private readonly previewBanner: PreviewBanner;
  private previewTimer: number | null = null;
  private readonly renderer: Renderer;

  private viewport: Viewport = { width: 0, height: 0 };
  private image: PreparedImageRef | null = null;
  private imageKey = '';
  private cfg = {
    difficulty: 'medium' as DifficultyLevel,
    rotateMode: true,
    showReference: false,
    referenceOpacity: 0.35,
  };
  private sourceCanvas: HTMLCanvasElement | null = null;
  private cached = new Map<number, CachedPiece>();
  private hitTester: HitTester | null = null;
  private input: InputController | null = null;
  private rafId = 0;
  private lastTs = 0;
  private lastEmitTs = 0;
  private woodBg: HTMLCanvasElement | null = null;
  private boardTile: HTMLCanvasElement;
  private readonly noiseTile: HTMLCanvasElement;
  private readonly snapAnims = new Map<
    number,
    { fx: number; fy: number; tx: number; ty: number; start: number; dur: number }
  >();
  private particles: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    color: string;
    size: number;
  }[] = [];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly ctx: CanvasRenderingContext2D,
  ) {
    this.store = new Store(initialIdleState());
    this.renderer = new Renderer(ctx);
    this.previewBanner = createPreviewBanner(() => this.showResult());
    this.overlay = createOverlay({
      onReplay: () => this.reset(),
      onExport: () => this.exportImage(),
      onClose: () => this.closeResult(),
    });
    this.overlayEl = this.overlay.el;
    this.previewEl = this.previewBanner.el;
    this.noiseTile = generateNoiseTexture(256, 256, 0.22);
    this.boardTile = generateBoardTile('cozy');
    this.applySize();
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
  }

  // ---- 生命周期 ----
  start(image: PreparedImageRef, opts: StartOptions): void {
    this.image = image;
    this.imageKey = opts.imageKey;
    this.cfg.difficulty = opts.difficulty;
    this.cfg.rotateMode = opts.rotateMode;
    this.build();
    if (!this.rafId) {
      this.lastTs = performance.now();
      this.rafId = requestAnimationFrame(this.loop);
    }
  }

  resize(): void {
    this.applySize();
    if (this.image) this.build();
  }

  /** 滚轮：画板区缩放，托盘区滚动。 */
  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const s = this.store.get();
    const bcx = s.boardX + s.boardW / 2;
    const bcy = s.boardY + s.boardH / 2;
    const halfW = (s.boardW / 2) * s.zoom;
    const halfH = (s.boardH / 2) * s.zoom;
    const inBoard = px >= bcx - halfW && px <= bcx + halfW && py >= bcy - halfH && py <= bcy + halfH;
    const inTray = px >= s.trayX && px <= s.trayX + s.trayW && py >= s.trayY && py <= s.trayY + s.trayH;
    if (inTray && !inBoard) {
      this.store.mutate((st) => {
        st.trayScroll = Math.min(st.maxTrayScroll, Math.max(0, st.trayScroll + e.deltaY));
      });
    } else {
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      this.store.mutate((st) => {
        st.zoom = Math.min(3, Math.max(0.4, st.zoom * factor));
      });
    }
  };

  /** 屏幕坐标 → 游戏世界坐标（板区 zoom 逆变换，托盘区 scroll 补偿，其余恒等）。 */
  private toWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const s = this.store.get();
    const bcx = s.boardX + s.boardW / 2;
    const bcy = s.boardY + s.boardH / 2;
    const halfW = (s.boardW / 2) * s.zoom;
    const halfH = (s.boardH / 2) * s.zoom;
    if (px >= bcx - halfW && px <= bcx + halfW && py >= bcy - halfH && py <= bcy + halfH) {
      return { x: bcx + (px - bcx) / s.zoom, y: bcy + (py - bcy) / s.zoom };
    }
    if (px >= s.trayX && px <= s.trayX + s.trayW && py >= s.trayY && py <= s.trayY + s.trayH) {
      return { x: px, y: py + s.trayScroll };
    }
    return { x: px, y: py };
  }

  private applySize(): void {
    const cssW = window.innerWidth;
    const cssH = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(cssW * dpr);
    this.canvas.height = Math.round(cssH * dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.viewport = { width: cssW, height: cssH };
    this.woodBg = generateBg(this.store.get().theme, cssW, cssH);
  }

  // ---- 构建：生成 / 散布 / 缓存 / 重建交互 ----
  private build(): void {
    if (!this.image) return;
    const layout = computeLayout(this.viewport, this.image.width, this.image.height);
    const { rows, cols } = gridForDifficulty(this.image.width, this.image.height, this.cfg.difficulty);
    const total = rows * cols;
    // 画板用布局原始尺寸（保持图片清晰，不压缩）；托盘装不下时改为滚动
    const pieceW = Math.max(20, Math.round(layout.boardW / cols));
    const pieceH = Math.max(20, Math.round(layout.boardH / rows));
    const boardW = pieceW * cols;
    const boardH = pieceH * rows;
    const boardX = layout.boardX + (layout.boardW - boardW) / 2;
    const boardY = layout.boardY + (layout.boardH - boardH) / 2;

    // 源图缩放到整数块尺寸的画板
    const source = createCanvas(boardW, boardH);
    get2D(source).drawImage(this.image.canvas, 0, 0, boardW, boardH);
    this.sourceCanvas = source;
    const srcImg: PieceSourceImage = { canvas: source, totalWidth: boardW, totalHeight: boardH };

    const seed = hashStringToSeed(`${this.imageKey}|${rows}x${cols}`);
    const puzzle = generatePuzzle(rows, cols, pieceW, pieceH, seed);
    const pad = Math.ceil(EAR_MAX * Math.max(pieceW, pieceH)) + 4;
    // 托盘缩略比例：使托盘至少能放 2 列（多列），画板保持全尺寸无损
    const trayScale = Math.max(0.2, Math.min(0.5, layout.trayW / (2 * (pieceW + 2 * pad))));
    const scatter = scatterWithSeed(
      {
        count: puzzle.pieces.length,
        trayX: layout.trayX,
        trayY: layout.trayY,
        trayW: layout.trayW,
        trayH: layout.trayH,
        pieceW: pieceW * trayScale,
        pieceH: pieceH * trayScale,
        pad: pad * trayScale,
        rotateMode: this.cfg.rotateMode,
      },
      seed ^ 0x9e3779b9,
    );
    // 托盘内容高度（缩略尺寸，可能超出可视区 → 滚动）
    const trayCellW = (pieceW + 2 * pad) * trayScale;
    const trayCellH = (pieceH + 2 * pad) * trayScale;
    const trayCols = Math.max(1, Math.floor(layout.trayW / trayCellW));
    const trayRows = Math.ceil(total / trayCols);
    const maxTrayScroll = Math.max(0, trayRows * trayCellH - layout.trayH);

    const runtime: RuntimePiece[] = puzzle.pieces.map((p, i) => ({
      ...p,
      currentX: scatter[i].x,
      currentY: scatter[i].y,
      rotation: scatter[i].rotation,
      placed: false,
      zIndex: i,
    }));

    this.cached.clear();
    for (const p of runtime) this.cached.set(p.id, cachePiece(srcImg, p, pieceW, pieceH, this.noiseTile));

    // 重建交互（hitTester 依赖 pieceW/H）
    this.input?.dispose();
    this.hitTester = new HitTester(this.ctx, pieceW, pieceH, boardX, boardY, boardW, boardH, trayScale);
    this.input = new InputController(this.canvas, this.store, this.hitTester, this.makeInputCallbacks());

    this.store.mutate((s) => {
      s.status = 'ready';
      s.image = this.image;
      s.imageKey = this.imageKey;
      s.config = { ...this.cfg };
      s.pieces = runtime;
      s.puzzleSeed = seed;
      s.pieceWidth = pieceW;
      s.pieceHeight = pieceH;
      s.boardX = boardX;
      s.boardY = boardY;
      s.boardW = boardW;
      s.boardH = boardH;
      s.trayX = layout.trayX;
      s.trayY = layout.trayY;
      s.trayW = layout.trayW;
      s.trayH = layout.trayH;
      s.startedAt = null;
      s.elapsedMs = 0;
      s.paused = false;
      s.moves = 0;
      s.placedCount = 0;
      s.topZ = total - 1;
      s.draggingId = null;
      s.dragOffsetX = 0;
      s.dragOffsetY = 0;
      s.hintsLeft = 3;
      s.hintTargetId = null;
      s.hintUntil = 0;
      s.history = [];
      s.trayScroll = 0;
      s.maxTrayScroll = maxTrayScroll;
      s.trayScale = trayScale;
    });
    this.overlay.hide();
    this.previewBanner.hide();
    if (this.previewTimer !== null) {
      window.clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
    this.completeInfo = null;
    this.store.emit();
  }

  private makeInputCallbacks(): InputCallbacks {
    return {
      getMetrics: () => {
        const s = this.store.get();
        return {
          pieceW: s.pieceWidth,
          pieceH: s.pieceHeight,
          trayScale: s.trayScale,
          boardX: s.boardX,
          boardY: s.boardY,
          boardW: s.boardW,
          boardH: s.boardH,
        };
      },
      getCanvasPoint: (e) => this.toWorld(e.clientX, e.clientY),
      onFirstInteraction: () => {
        this.store.mutate((s) => {
          s.status = 'playing';
          s.startedAt = performance.now();
        });
        this.store.emit();
      },
      onPlaced: (piece, fromX, fromY) => {
        const st = this.store.get();
        this.startSnapAnim(piece.id, fromX, fromY, st.boardX + piece.baseX, st.boardY + piece.baseY);
        sfx.snap();
        if (navigator.vibrate) navigator.vibrate(18);
      },
      onPick: () => sfx.pick(),
      onComplete: () => this.handleComplete(),
    };
  }

  private handleComplete(): void {
    this.store.mutate((s) => {
      s.status = 'completed';
    });
    const s = this.store.get();
    this.spawnFireworks(s.boardX + s.boardW / 2, s.boardY + s.boardH / 2);
    sfx.complete();
    const key = bestRecordKey(s.imageKey, s.config.difficulty, s.config.rotateMode);
    const prev = loadBest(key);
    const cand = { timeMs: s.elapsedMs, moves: s.moves };
    const best = isBetter(prev, cand);
    if (best) saveBest(key, cand);
    // 先进入预览阶段（展示拼合成品），由用户点“查看成绩”进入成绩弹层
    this.completeInfo = { timeMs: s.elapsedMs, moves: s.moves, best };
    this.previewBanner.show();
    this.store.emit();
  }

  private showResult(): void {
    if (this.previewTimer !== null) {
      window.clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
    this.previewBanner.hide();
    if (this.completeInfo) {
      this.overlay.show(this.completeInfo);
    }
  }

  /** 关闭成绩弹层，回到游戏画面（保留底部入口可再次打开成绩）。 */
  private closeResult(): void {
    this.overlay.hide();
    this.previewBanner.show();
  }

  // ---- 主循环 ----
  private loop = (ts: number): void => {
    const dt = Math.min(ts - this.lastTs, 100); // clamp：防止后台回来跳秒
    this.lastTs = ts;
    // 提示超时清理
    const s0 = this.store.get();
    if (s0.hintTargetId !== null && performance.now() >= s0.hintUntil) {
      this.store.mutate((st) => {
        st.hintTargetId = null;
      });
    }
    const s = this.store.get();
    if (s.status === 'playing' && !s.paused) {
      this.store.mutate((st) => {
        st.elapsedMs += dt;
      });
      if (ts - this.lastEmitTs > 250) {
        this.lastEmitTs = ts;
        this.store.emit(); // 计时 UI 节流更新
      }
    }
    this.updateParticles(dt);
    this.render();
    this.rafId = requestAnimationFrame(this.loop);
  };

  private render(): void {
    const s = this.store.get();
    // 背景：木纹桌面
    if (this.woodBg) {
      this.ctx.drawImage(this.woodBg, 0, 0, this.viewport.width, this.viewport.height);
    } else {
      this.renderer.clear(this.viewport, '#241710');
    }

    const now = performance.now();
    const order = s.pieces.slice().sort((a, b) => a.zIndex - b.zIndex);
    // 碎片中心是否落在画板内（决定它跟随画板缩放，还是按托盘固定大小）
    const onBoard = (p: RuntimePiece): boolean => {
      const cx = p.currentX + s.pieceWidth / 2;
      const cy = p.currentY + s.pieceHeight / 2;
      return (
        cx >= s.boardX &&
        cx <= s.boardX + s.boardW &&
        cy >= s.boardY &&
        cy <= s.boardY + s.boardH
      );
    };
    const drawPieceAt = (p: RuntimePiece, baseScale: number): void => {
      const cached = this.cached.get(p.id);
      if (!cached) return;
      let x = p.currentX;
      let y = p.currentY;
      let scale = baseScale;
      if (s.draggingId === p.id) {
        scale = baseScale * 1.08;
      } else {
        const anim = this.snapAnims.get(p.id);
        if (anim) {
          const t = Math.min(1, (now - anim.start) / anim.dur);
          const e = easeOutBack(t);
          x = anim.fx + (anim.tx - anim.fx) * e;
          y = anim.fy + (anim.ty - anim.fy) * e;
          if (t >= 1) this.snapAnims.delete(p.id);
        }
      }
      this.renderer.drawPieceRotated(cached, x, y, p.rotation, scale);
    };

    // === 画板区（可缩放）：以板中心缩放，仅板上的碎片随之放大/缩小 ===
    this.ctx.save();
    const bcx = s.boardX + s.boardW / 2;
    const bcy = s.boardY + s.boardH / 2;
    this.ctx.translate(bcx, bcy);
    this.ctx.scale(s.zoom, s.zoom);
    this.ctx.translate(-bcx, -bcy);

    if (s.boardW > 0) {
      this.ctx.save();
      this.ctx.drawImage(this.boardTile, s.boardX, s.boardY, s.boardW, s.boardH);
      this.ctx.strokeStyle = 'rgba(0,0,0,0.45)';
      this.ctx.lineWidth = 10;
      this.ctx.strokeRect(s.boardX, s.boardY, s.boardW, s.boardH);
      this.ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(s.boardX + 1, s.boardY + 1, s.boardW - 2, s.boardH - 2);
      this.ctx.restore();

      if (s.config.showReference && this.sourceCanvas) {
        this.ctx.save();
        this.ctx.globalAlpha = s.config.referenceOpacity;
        this.ctx.drawImage(this.sourceCanvas, s.boardX, s.boardY, s.boardW, s.boardH);
        this.ctx.restore();
      }
    }
    for (const p of order) if (onBoard(p)) drawPieceAt(p, 1);
    this.drawHint(s);
    this.drawParticles();
    this.ctx.restore();

    // === 托盘区（固定区域，内容可滚动）：仅托盘里的碎片 ===
    if (s.trayW > 0) {
      this.ctx.save();
      this.ctx.drawImage(this.boardTile, s.trayX, s.trayY, s.trayW, s.trayH);
      this.ctx.fillStyle = 'rgba(0,0,0,0.12)';
      this.ctx.fillRect(s.trayX, s.trayY, s.trayW, s.trayH);
      this.ctx.strokeStyle = 'rgba(0,0,0,0.3)';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(s.trayX, s.trayY, s.trayW, s.trayH);
      // 滚动条提示（可滚动时）
      if (s.maxTrayScroll > 0) {
        const ratio = s.trayH / (s.trayH + s.maxTrayScroll);
        const thumbH = s.trayH * ratio;
        const thumbY = s.trayY + (s.trayH - thumbH) * (s.trayScroll / s.maxTrayScroll);
        this.ctx.fillStyle = 'rgba(255,255,255,0.2)';
        this.ctx.fillRect(s.trayX + s.trayW - 6, thumbY, 4, thumbH);
      }
      this.ctx.restore();

      // 碎片（裁剪到托盘可视区，排除拖拽中的块——拖拽块单独顶层绘制，保证跨区可见）
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect(s.trayX, s.trayY, s.trayW, s.trayH);
      this.ctx.clip();
      this.ctx.translate(0, -s.trayScroll);
      for (const p of order) if (!onBoard(p) && p.id !== s.draggingId) drawPieceAt(p, s.trayScale);
      this.ctx.restore();
    }

    // 拖拽中的块：若在托盘/中间地带，按滚动偏移但不裁剪（跨区拖拽全程可见）
    if (s.draggingId !== null) {
      const dp = s.pieces[s.draggingId];
      if (dp && !onBoard(dp)) {
        this.ctx.save();
        this.ctx.translate(0, -s.trayScroll);
        drawPieceAt(dp, s.trayScale);
        this.ctx.restore();
      }
    }
  }

  private drawHint(s: GameState): void {
    if (s.hintTargetId === null) return;
    const now = performance.now();
    if (now >= s.hintUntil) return;
    const p = s.pieces[s.hintTargetId];
    if (!p) return;
    const path = buildPiecePath2D(p.edges, s.pieceWidth, s.pieceHeight);
    const pulse = 0.5 + 0.5 * Math.sin(now / 120);
    this.ctx.save();
    this.ctx.translate(s.boardX + p.baseX, s.boardY + p.baseY);
    this.ctx.lineWidth = 3;
    this.ctx.strokeStyle = `rgba(255,213,74,${0.4 + 0.6 * pulse})`;
    this.ctx.shadowColor = 'rgba(255,213,74,0.9)';
    this.ctx.shadowBlur = 14;
    this.ctx.stroke(path);
    this.ctx.restore();
  }

  // ---- GameActions ----
  setDifficulty(level: DifficultyLevel): void {
    this.cfg.difficulty = level;
    this.build();
  }

  toggleRotate(enabled: boolean): void {
    this.cfg.rotateMode = enabled;
    this.build();
  }

  toggleReference(enabled: boolean): void {
    this.cfg.showReference = enabled;
    this.store.mutate((s) => {
      s.config.showReference = enabled;
    });
    this.store.emit();
  }

  toggleMute(): void {
    this.store.mutate((s) => {
      s.muted = !s.muted;
    });
    sfx.setMuted(this.store.get().muted);
    this.store.emit();
  }

  /** 导出拼好的完整图为 PNG（下载）。 */
  exportImage(): void {
    if (!this.sourceCanvas) return;
    this.sourceCanvas.toBlob((b) => {
      if (!b) return;
      const url = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pintu-${this.imageKey}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  }

  /** 生成当前局面的分享链接（内置示例图可分享；上传图为本地不可分享）。 */
  getShareUrl(): string {
    const s = this.store.get();
    let key: string | null = null;
    if (s.imageKey.startsWith('picsum-')) key = s.imageKey; // 带 seed，打开可复现
    else if (s.imageKey.startsWith('sample-')) key = s.imageKey.slice(7);
    // upload- 为本地文件，不可分享
    const base = location.origin + location.pathname;
    const p = new URLSearchParams();
    if (key) p.set('image', key);
    p.set('diff', s.config.difficulty);
    p.set('rotate', s.config.rotateMode ? '1' : '0');
    if (s.config.showReference) p.set('ref', '1');
    return `${base}?${p.toString()}`;
  }

  share(): void {
    const url = this.getShareUrl();
    const nav = navigator as Navigator & { clipboard?: { writeText(t: string): Promise<void> } };
    if (nav.clipboard) {
      nav.clipboard.writeText(url).then(
        () => showToast('分享链接已复制到剪贴板'),
        () => showToast('复制失败，请手动复制'),
      );
    } else {
      showToast('分享链接：' + url, 5000);
    }
  }

  setTheme(theme: Theme): void {
    this.store.mutate((s) => {
      s.theme = theme;
    });
    this.boardTile = generateBoardTile(theme);
    this.woodBg = generateBg(theme, this.viewport.width, this.viewport.height);
    this.store.emit();
  }

  cycleTheme(): void {
    const order: Theme[] = ['cozy', 'dark', 'light'];
    const cur = this.store.get().theme;
    this.setTheme(order[(order.indexOf(cur) + 1) % order.length]);
  }

  reset(): void {
    this.build();
  }

  useHint(): void {
    const s = this.store.get();
    if (s.hintsLeft <= 0) return;
    const target = pickHintTarget(s.pieces);
    if (!target) return;
    this.store.mutate((st) => {
      st.hintsLeft -= 1;
      st.hintTargetId = target.id;
      st.hintUntil = performance.now() + 2500;
    });
    this.store.emit();
  }

  undo(): void {
    const s = this.store.get();
    const entry: HistoryEntry | undefined = s.history[s.history.length - 1];
    if (!entry) return;
    this.store.mutate((st) => {
      const p = st.pieces[entry.id];
      if (!p) return;
      st.placedCount += computeUndoDelta(p.placed, entry.placed);
      p.currentX = entry.x;
      p.currentY = entry.y;
      p.rotation = entry.rot;
      p.placed = entry.placed;
      st.history.pop();
      st.draggingId = null;
    });
    this.store.emit();
  }

  private startSnapAnim(id: number, fx: number, fy: number, tx: number, ty: number): void {
    this.snapAnims.set(id, { fx, fy, tx, ty, start: performance.now(), dur: 220 });
  }

  private spawnFireworks(cx: number, cy: number): void {
    const colors = ['#ffd54a', '#ff7a59', '#ff4d6d', '#7be0ff', '#9dff7b', '#ffffff'];
    for (let i = 0; i < 90; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = 80 + Math.random() * 260;
      this.particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp,
        life: 900 + Math.random() * 700,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 2 + Math.random() * 3,
      });
    }
  }

  private updateParticles(dt: number): void {
    if (this.particles.length === 0) return;
    const dts = dt / 1000;
    const next: typeof this.particles = [];
    for (const p of this.particles) {
      p.life -= dt;
      if (p.life <= 0) continue;
      p.vy += 380 * dts;
      p.x += p.vx * dts;
      p.y += p.vy * dts;
      p.vx *= 0.99;
      next.push(p);
    }
    this.particles = next;
  }

  private drawParticles(): void {
    if (this.particles.length === 0) return;
    const ctx = this.ctx;
    ctx.save();
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life / 500));
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function generateBg(theme: Theme, w: number, h: number): HTMLCanvasElement {
  if (theme === 'dark') {
    const c = createCanvas(w, h);
    const ctx = get2D(c);
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#1c1830');
    g.addColorStop(1, '#100c1a');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    return c;
  }
  if (theme === 'light') {
    return generateWoodTexture(w, h, { base: '#d9c4a6', dark: '#b89f7e', light: '#ece0c8', graining: 0.4 });
  }
  return generateWoodTexture(w, h, { base: '#33241a', dark: '#1f150e', light: '#3f2e20', graining: 0.6 });
}

function generateBoardTile(theme: Theme): HTMLCanvasElement {
  if (theme === 'dark') {
    return generateWoodTexture(512, 512, { base: '#2a2438', dark: '#1a1525', light: '#3a304f', graining: 0.4 });
  }
  if (theme === 'light') {
    return generateWoodTexture(512, 512, { base: '#b8a07e', dark: '#8f7a5a', light: '#d2bf9c', graining: 0.4 });
  }
  return generateWoodTexture(512, 512, { base: '#5a4332', dark: '#3e2c1e', light: '#6d5340', graining: 0.4 });
}
